// apps-kb-navigation.js - Cursor movement logic (REFACTORED)

import state from './apps-state.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';
import * as cursor from './apps-kb-cursor.js';
import * as scroll from './apps-kb-scroll.js';
import * as modal from './apps-kb-modal.js';
import * as nav from './apps-navigation.js';
import * as navLogic from './apps-kb-navigation-logic.js';

export function moveCursor(direction, updateKeyboardDragIndicators) {
    if (!keyboardState.virtualCursor.active) {
        moveCursorInitial(direction);
        return;
    }
    
    console.log('moveCursor called, direction:', direction, 'current:', {
        rank: keyboardState.virtualCursor.rank,
        file: keyboardState.virtualCursor.file,
        isOnDeleteButton: keyboardState.virtualCursor.isOnDeleteButton,
        isInModalZone: keyboardState.virtualCursor.isInModalZone
    });
    
    // Handle delete button (empty screen)
    if (keyboardState.virtualCursor.isOnDeleteButton) {
        handleDeleteButtonNavigation(direction);
        return;
    }
    
    // Handle modal zones
    if (keyboardState.virtualCursor.isInModalZone) {
        modal.handleModalZoneMovement(direction);
        return;
    }
    
    // Get context
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    const currentScreen = helpers.getCurrentScreen();
    const inMoveMode = keyboardState.moveMode.active;
    
    // Move mode horizontal navigation is handled in move-mode module
    if (inMoveMode && (direction === 'left' || direction === 'right')) {
        return;
    }
    
    // Get current position
    let currentRank = keyboardState.virtualCursor.rank;
    let currentFile = keyboardState.virtualCursor.file;
    
    // Get next position using logical navigation
    const nextPos = navLogic.getNextNavigablePosition(
        currentRank,
        currentFile,
        direction,
        !inMoveMode, // browseMode = !inMoveMode
        inMoveMode,
        icons,
        layout,
        isFolder,
        currentScreen,
        keyboardState.moveMode.draggedIcon || null
    );
    
    if (!nextPos) {
        console.log('No valid next position');
        return;
    }
    
    console.log('Next position:', nextPos);
    
    // Handle special positions
    if (nextPos.rank === -1) {
        // Top modal zone
        handleTopZoneNavigation(nextPos.file);
        return;
    }
    
    if (nextPos.rank === -0.5 && isFolder) {
        // Folder title
        handleFolderTitleNavigation();
        return;
    }
    
    const maxRank = navLogic.getMaxRank(layout, isFolder, inMoveMode);
    if (nextPos.rank > maxRank) {
        // Bottom modal zone
        handleBottomZoneNavigation(nextPos.file);
        return;
    }
    
    if (nextPos.file === -1) {
        // Left scroll zone
        handleLeftScrollZone(nextPos.rank);
        return;
    }
    
    if (nextPos.file >= navLogic.getMaxFile(layout) + 1) {
        // Right scroll zone
        handleRightScrollZone(nextPos.rank);
        return;
    }
    
    // Regular position - show cursor
    cursor.showVirtualCursor(nextPos.rank, nextPos.file);
    
    // Update drag indicators if in move mode
    if (inMoveMode) {
        updateKeyboardDragIndicators(nextPos.rank, nextPos.file);
    }
}

// ===== HELPER FUNCTIONS =====

function roundInsertPositionForVerticalMovement(file, insertType) {
    if (insertType === 'between') {
        // For insert-between at X.5, round down to X
        return Math.floor(file);
    } else if (insertType === 'after') {
        // For insert-after, already at integer position
        return Math.floor(file);
    } else if (insertType === 'before') {
        // For insert-before at X:
        // - If X > 0: gap is between X-1 and X, so round down to X-1
        // - If X == 0: gap is at beginning, so stay at 0
        const currentFile = Math.floor(file);
        return currentFile > 0 ? currentFile - 1 : 0;
    }
    return Math.floor(file);
}

function handleDeleteButtonNavigation(direction) {
    console.log('Handling delete button navigation:', direction);
    
    keyboardState.virtualCursor.isOnDeleteButton = false;
    const currentScreen = helpers.getCurrentScreen();
    const isFolder = !!state.openFolder;
    
    if (direction === 'left') {
        if (currentScreen > 0) {
            cursor.hideVirtualCursor();
            scroll.showScrollZoneHover('left');
            return;
        }
        // Stay on delete button on first screen
        cursor.showVirtualCursor(0, 0);
        keyboardState.virtualCursor.isOnDeleteButton = true;
        return;
    }
    
    if (direction === 'right') {
        const allIcons = helpers.getCurrentIcons();
        const layout = helpers.getLayout();
        const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
        const maxRegion = Math.max(...allIcons.map(icon => icon.region), -1);
        const maxScreen = Math.floor(maxRegion / regionsPerScreen);
        
        if (currentScreen < maxScreen) {
            cursor.hideVirtualCursor();
            scroll.showScrollZoneHover('right');
            return;
        }
        // Stay on delete button on last screen
        cursor.showVirtualCursor(0, 0);
        keyboardState.virtualCursor.isOnDeleteButton = true;
        return;
    }
    
    if (direction === 'up') {
        if (isFolder) {
            // Go to folder exit (top zone)
            cursor.hideVirtualCursor();
            keyboardState.virtualCursor.active = true;
            keyboardState.virtualCursor.isInModalZone = true;
            keyboardState.virtualCursor.modalZonePosition = 'top';
            keyboardState.virtualCursor.rank = -1;
            modal.highlightModalZone('top');
            return;
        }
        // Stay on delete button
        cursor.showVirtualCursor(0, 0);
        keyboardState.virtualCursor.isOnDeleteButton = true;
        return;
    }
    
    if (direction === 'down') {
        if (isFolder) {
            // Go to bottom modal zone
            cursor.hideVirtualCursor();
            keyboardState.virtualCursor.active = true;
            keyboardState.virtualCursor.isInModalZone = true;
            keyboardState.virtualCursor.modalZonePosition = 'bottom';
            const layout = helpers.getLayout();
            const maxRank = navLogic.getMaxRank(layout, true, false);
            keyboardState.virtualCursor.rank = maxRank + 1;
            keyboardState.virtualCursor.file = 0;
            modal.highlightModalZone('bottom');
            return;
        }
        // Stay on delete button
        cursor.showVirtualCursor(0, 0);
        keyboardState.virtualCursor.isOnDeleteButton = true;
        return;
    }
}

function handleTopZoneNavigation(file) {
    cursor.hideVirtualCursor();
    keyboardState.virtualCursor.active = true;
    keyboardState.virtualCursor.isInModalZone = true;
    keyboardState.virtualCursor.modalZonePosition = 'top';
    keyboardState.virtualCursor.rank = -1;
    keyboardState.virtualCursor.file = file;
    modal.highlightModalZone('top');
}

function handleFolderTitleNavigation() {
    cursor.hideVirtualCursor();
    keyboardState.virtualCursor.rank = -0.5;
    // keyboardState.virtualCursor.file = 0;
    
    const folderTitle = document.getElementById('folderTitle');
    if (folderTitle) {
        folderTitle.focus();
    }
}

function handleBottomZoneNavigation(file) {
    cursor.hideVirtualCursor();
    keyboardState.virtualCursor.active = true;
    keyboardState.virtualCursor.isInModalZone = true;
    keyboardState.virtualCursor.modalZonePosition = 'bottom';
    
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const inMoveMode = keyboardState.moveMode.active;
    const maxRank = navLogic.getMaxRank(layout, isFolder, inMoveMode);
    
    keyboardState.virtualCursor.rank = maxRank + 1;
    keyboardState.virtualCursor.file = file;
    modal.highlightModalZone('bottom');
}

function handleLeftScrollZone(rank) {
    keyboardState.virtualCursor.rank = rank;
    keyboardState.virtualCursor.file = -1;
    
    // In move mode, show the cursor/ghost at the scroll zone position
    if (keyboardState.moveMode.active) {
        cursor.showVirtualCursor(rank, -1);
    } else {
        cursor.hideVirtualCursor();
    }
    
    scroll.showScrollZoneHover('left');
}

function handleRightScrollZone(rank) {
    const layout = helpers.getLayout();
    const maxFile = navLogic.getMaxFile(layout);
    
    keyboardState.virtualCursor.rank = rank;
    keyboardState.virtualCursor.file = maxFile + 1;
    
    // In move mode, show the cursor/ghost at the scroll zone position
    if (keyboardState.moveMode.active) {
        cursor.showVirtualCursor(rank, maxFile + 1);
    } else {
        cursor.hideVirtualCursor();
    }
    
    scroll.showScrollZoneHover('right');
}

// ===== INITIAL CURSOR POSITIONING =====

export function moveCursorInitial(direction) {
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const icons = helpers.getCurrentIcons();
    const currentScreen = helpers.getCurrentScreen();
    
    console.log('moveCursorInitial:', direction);
    
    // Use logical navigation to get initial position
    const initialPos = navLogic.getInitialCursorPosition(direction, icons, layout, isFolder, currentScreen);
    
    if (initialPos) {
        cursor.showVirtualCursor(initialPos.rank, initialPos.file);
    }
}

// ===== FOLDER NAVIGATION HANDLERS =====

export function handleFolderOpen() {
    if (!state.openFolder) return;
    
    setTimeout(() => {
        const layout = helpers.getLayout();
        const icons = helpers.getCurrentIcons();
        const isFolder = true;
        const currentScreen = 0; // Folders always start at screen 0
        
        const minRank = navLogic.getMinOccupiedRank(icons, layout, isFolder, currentScreen);
        const minFile = navLogic.getMinOccupiedFile(icons, layout, isFolder, currentScreen);
        
        if (minRank !== null && minFile !== null) {
            cursor.showVirtualCursor(minRank, minFile);
        } else {
            cursor.showVirtualCursor(0, 0);
        }
    }, 100);
}

export function handleFolderClose(closedFolderId) {
    if (!closedFolderId) return;
    
    const folderIcon = window.icons.find(icon => icon.id === closedFolderId);
    
    if (folderIcon) {
        setTimeout(() => {
            const layout = helpers.getLayout();
            const visualPos = helpers.regionPositionToRankFile(folderIcon.region, folderIcon.position, layout, false);
            
            if (visualPos.screen !== undefined && visualPos.screen !== state.currentScreen) {
                nav.goToScreen(visualPos.screen);
            }
            
            cursor.showVirtualCursor(visualPos.rank, visualPos.file);
        }, 100);
    }
}
